/*
 * Guarda do par peça + bordado no carrinho — ADR-0012.
 *
 * A grade de kit grava o bordado como uma linha de cobrança separada, ligada à
 * peça por `_bordado_de`. A PDP com preview (ADR-0017) grava o mesmo par, mas
 * a peça declara a referência em `_bordado_ref` em vez de `_kit`/`_kit_posicao`;
 * a guarda cobre os dois. Nenhuma UI de carrinho da loja conhece esse vínculo:
 * o cliente apaga uma ponta e a outra fica órfã — o bordado sozinho, que é um
 * produto comprável, ou a peça cobrando só o tecido com a personalização ainda
 * escrita na etiqueta. Esta guarda reconcilia o par depois de qualquer
 * alteração, venha ela do tema, do Kaching Cart, do iCart ou da página /cart.
 *
 * Observa por `PerformanceObserver` em vez de embrulhar `window.fetch` porque o
 * Kaching (`__kachingCartOriginalFetch`) e o snippet do CartPanda já embrulham.
 * Uma terceira camada de patch é exatamente o tipo de coisa que derruba
 * carrinho em produção.
 */

(() => {
  const ROTAS_DE_ALTERACAO = /\/cart\/(add|change|update|clear)/;
  const TENTATIVAS = 10;
  const PROPRIEDADE_LOGO = 'Bordado logo';

  // A arte entra junto com a marcação de logo e tem de sair junto. Deixar a arte
  // sozinha na peça manda o arquivo para a produção sem ninguém ter pago por ele,
  // que é exatamente o erro que esta guarda existe para impedir.
  const PROPRIEDADES_DE_LOGO = [PROPRIEDADE_LOGO, 'Upload logo'];

  // O nome do ícone escolhido (ADR-0027). Sai junto com a cobrança do ícone.
  const PROPRIEDADE_ICONE = 'Bordado ícone';
  const PROPRIEDADES_DE_ICONE = [PROPRIEDADE_ICONE];

  // A marcação de que a peça é bordada vale para qualquer bordado e sobrevive
  // enquanto sobrar um. A cor da linha vale para o texto e, se ficou na peça, para
  // o logo; nunca para o ícone sozinho: sem texto nem logo, ela não fica no pedido.
  // Logo sem texto (D30) sai do modal sem `Cores`; a guarda só não a apaga.
  const PROPRIEDADE_MARCACAO = 'Adicione bordado';
  const PROPRIEDADE_COR = 'Cores';

  let reconciliando = false;
  let suspensa = false;
  let agendamento = null;
  let textosDoAviso = {};

  /**
   * Liga a guarda: passa a observar alterações e confere o que já está no
   * carrinho quando a página abre com itens.
   * @param {HTMLElement|null} script
   */
  function iniciar(script) {
    textosDoAviso = script?.dataset ?? {};

    new PerformanceObserver((lista) => {
      if (lista.getEntries().some((entrada) => ROTAS_DE_ALTERACAO.test(entrada.name))) agendar();
    }).observe({ type: 'resource', buffered: true });

    // A grade avisa quando está montando um kit. Um envio com arte de logo não
    // cabe numa requisição só, e entre uma e outra o carrinho passa por estados
    // que daqui são indistinguíveis de uma divergência de verdade — peça
    // personalizada ainda sem a linha que a cobra. Reconciliar no meio desfaria
    // o que está sendo montado, então a guarda espera e age uma vez, no fim.
    document.addEventListener('kit:envio-iniciado', () => {
      suspensa = true;
      clearTimeout(agendamento);
    });

    document.addEventListener('kit:envio-concluido', () => {
      suspensa = false;
      agendar();
    });

    if (Number(script?.dataset.itens) > 0) agendar();
  }

  /** Agenda uma reconciliação, juntando alterações em rajada numa só. */
  function agendar() {
    if (reconciliando || suspensa) return;

    clearTimeout(agendamento);
    agendamento = setTimeout(executar, 250);
  }

  /** Reconcilia e, se mexeu em alguma linha, pede o redesenho do carrinho. */
  async function executar() {
    reconciliando = true;

    try {
      const { carrinho, corrigiu, motivos } = await reconciliar();

      if (corrigiu) {
        avisarCorrecao(motivos);
        redesenhar(carrinho);
      }
    } catch (erro) {
      // Carrinho inconsistente é ruim; carrinho travado é pior.
      console.warn('[kit] não foi possível reconciliar o carrinho', erro);
    } finally {
      reconciliando = false;
    }
  }

  /**
   * Aplica uma divergência por vez e relê o carrinho, porque cada alteração
   * renumera as linhas.
   * @param {number} restantes
   * @returns {Promise<{carrinho: object, corrigiu: boolean, motivos: string[]}>}
   */
  async function reconciliar(restantes = TENTATIVAS) {
    const carrinho = await pedir('cart.js');
    const divergencia = restantes > 0 ? acharDivergencia(carrinho.items) : null;
    if (!divergencia) return { carrinho, corrigiu: false, motivos: [] };

    // O motivo é nosso; o `/cart/change.js` recebe só `line` + `quantity|properties`.
    const { motivo } = divergencia;
    const { line, quantity, properties } = divergencia;
    const correcao = { line, ...(quantity !== undefined && { quantity }), ...(properties && { properties }) };
    await pedir('cart/change.js', correcao);
    const seguinte = await reconciliar(restantes - 1);

    return { carrinho: seguinte.carrinho, corrigiu: true, motivos: [motivo, ...seguinte.motivos] };
  }

  /**
   * Primeira divergência do carrinho, já no formato do `/cart/change.js`.
   *
   * Uma peça pode ter até três cobranças — o bordado de nome (a linha sem tipo),
   * o de logo e o de ícone — e elas somem independentes, então são quatro
   * regras: cobrança sem peça sai do carrinho; cobrança com quantidade diferente
   * da peça acompanha a peça; cobrança de logo ou de ícone cuja peça perdeu a
   * property daquele bordado sai; peça que ficou sem uma das cobranças perde só
   * as properties daquela cobrança.
   *
   * Nunca devolve `quantity: 0` para uma linha de peça: a guarda só remove
   * cobrança. Peça que sumiu é problema do envio (`kit-conferencia.js`), não dela.
   *
   * O `motivo` acompanha a correção para o aviso e a telemetria como property
   * NÃO enumerável (`comMotivo`): o contrato `{line, quantity|properties}` que o
   * `/cart/change.js` recebe continua intacto — nem `JSON.stringify` nem o spread
   * o levam —, e `reconciliar` ainda assim o separa antes de mandar o corpo.
   *
   * Os bordados são independentes: são serviços cobrados em separado, e tirar um
   * do carrinho não cancela os outros. A peça só com ícone (ADR-0027) é válida e
   * não tem linha de nome. Só quando todas as cobranças somem é que a peça volta
   * a ser peça sem personalização nenhuma.
   *
   * @param {object[]} itens
   * @returns {({line: number, motivo: string} & ({quantity: number}|{properties: object}))|null}
   */
  function acharDivergencia(itens) {
    const pecas = new Map();
    itens.forEach((item) => {
      const referencia = referenciaDaPeca(item);
      if (referencia) pecas.set(referencia, item);
    });

    for (const [indice, item] of itens.entries()) {
      const referencia = item.properties?._bordado_de;
      if (!referencia) continue;

      const peca = pecas.get(referencia);
      if (!peca) return comMotivo('cobranca_sem_peca', { line: indice + 1, quantity: 0 });
      if (ehLogo(item) && !peca.properties?.[PROPRIEDADE_LOGO]) {
        return comMotivo('cobranca_logo_sem_marcacao', { line: indice + 1, quantity: 0 });
      }
      if (ehIcone(item) && !peca.properties?.[PROPRIEDADE_ICONE]) {
        return comMotivo('cobranca_icone_sem_marcacao', { line: indice + 1, quantity: 0 });
      }
      if (peca.quantity !== item.quantity) {
        return comMotivo('cobranca_quantidade', { line: indice + 1, quantity: peca.quantity });
      }
    }

    const cobradas = referenciasCobradas(itens, ehNome);
    const cobradasComLogo = referenciasCobradas(itens, ehLogo);
    const cobradasComIcone = referenciasCobradas(itens, ehIcone);

    for (const [indice, item] of itens.entries()) {
      const referencia = referenciaDaPeca(item);
      if (!referencia) continue;

      const propriedades = item.properties ?? {};
      const nomePago = cobradas.has(referencia);
      const logoPago = cobradasComLogo.has(referencia);
      const iconePago = cobradasComIcone.has(referencia);

      // Nenhuma cobrança de pé: a peça volta a ser peça.
      if (!nomePago && !logoPago && !iconePago) {
        if (!temPersonalizacao(propriedades)) continue;

        // Fica só o que é de controle (`_kit`/`_kit_posicao` da grade,
        // `_bordado_ref` da PDP): é o que mantém a peça reconhecível.
        return comMotivo('peca_sem_cobranca', {
          line: indice + 1,
          properties: Object.fromEntries(Object.entries(propriedades).filter(([nome]) => nome.startsWith('_'))),
        });
      }

      // Alguma sobrou: cai só o que pertence às que saíram. A cor da linha vale
      // para o texto e para o logo; só o ícone de pé não a justifica.
      const orfas = [
        ...(nomePago ? [] : propriedadesDeNome(propriedades)),
        ...(logoPago ? [] : PROPRIEDADES_DE_LOGO),
        ...(iconePago ? [] : PROPRIEDADES_DE_ICONE),
        ...(nomePago || logoPago ? [] : [PROPRIEDADE_COR]),
      ].filter((nome) => propriedades[nome] !== undefined);

      if (orfas.length > 0) {
        const restante = Object.fromEntries(
          Object.entries(propriedades).filter(([nome]) => !orfas.includes(nome))
        );

        return comMotivo('peca_sem_cobranca', { line: indice + 1, properties: restante });
      }
    }

    return null;
  }

  /**
   * Anexa o motivo à correção sem torná-lo parte do corpo do `/cart/change.js`.
   * @param {string} motivo
   * @param {object} correcao
   * @returns {object}
   */
  function comMotivo(motivo, correcao) {
    return Object.defineProperty(correcao, 'motivo', { value: motivo, enumerable: false });
  }

  /**
   * Properties que pertencem ao bordado de nome: tudo que a cliente vê e que não
   * é do logo nem comum aos dois. Vem por exclusão porque os campos de texto são
   * publicados pelo Live Product Options e mudam sem o tema saber.
   * @param {Record<string, string>} propriedades
   * @returns {string[]}
   */
  function propriedadesDeNome(propriedades) {
    return Object.keys(propriedades).filter(
      (nome) =>
        !nome.startsWith('_') &&
        !PROPRIEDADES_DE_LOGO.includes(nome) &&
        !PROPRIEDADES_DE_ICONE.includes(nome) &&
        nome !== PROPRIEDADE_COR &&
        nome !== PROPRIEDADE_MARCACAO
    );
  }

  /**
   * Diz se a linha é a cobrança do logo. Logo e ícone são marcados por
   * `_bordado_tipo`; a de nome nasceu antes de existir a distinção e continua
   * sendo a linha sem tipo (`ehNome`).
   * @param {object} item
   * @returns {boolean}
   */
  function ehLogo(item) {
    return item.properties?._bordado_tipo === 'logo';
  }

  /**
   * Diz se a linha é a cobrança do bordado de ícone (ADR-0027).
   * @param {object} item
   * @returns {boolean}
   */
  function ehIcone(item) {
    return item.properties?._bordado_tipo === 'icone';
  }

  /**
   * Diz se a linha é a cobrança do bordado de nome: a que não é de logo nem de
   * ícone. Um tipo que a guarda não conhece também cai aqui, como antes.
   * @param {object} item
   * @returns {boolean}
   */
  function ehNome(item) {
    return !ehLogo(item) && !ehIcone(item);
  }

  /**
   * Referências que ainda têm cobrança viva no carrinho, de um tipo só.
   * @param {object[]} itens
   * @param {(item: object) => boolean} aceita
   * @returns {Set<string>}
   */
  function referenciasCobradas(itens, aceita) {
    const referencias = itens
      .filter((item) => item.properties?._bordado_de && aceita(item))
      .map((item) => item.properties._bordado_de);

    return new Set(referencias);
  }

  /**
   * Referência que liga a peça à sua linha de bordado, no mesmo formato que
   * `_bordado_de` grava. A PDP a declara direto em `_bordado_ref`; a grade de
   * kit a deriva de `_kit` e `_kit_posicao`.
   * @param {object} item
   * @returns {string|null}
   */
  function referenciaDaPeca(item) {
    if (item.properties?._bordado_ref) return item.properties._bordado_ref;

    const { _kit: kit, _kit_posicao: posicao } = item.properties ?? {};

    return kit && posicao ? `${kit}#${posicao}` : null;
  }

  /**
   * Diz se a linha ainda carrega personalização visível ao cliente. As
   * properties de controle começam com `_` e não contam.
   * @param {Record<string, string>} propriedades
   * @returns {boolean}
   */
  function temPersonalizacao(propriedades) {
    return Object.keys(propriedades ?? {}).some((chave) => !chave.startsWith('_'));
  }

  /**
   * A guarda nunca corrige em silêncio (ENN-96): a correção vai ao console, ao
   * dataLayer (só evento; nenhuma tag do GTM o consome ainda) e à tela. Um aviso
   * por reconciliação, com o primeiro motivo.
   * @param {string[]} motivos
   */
  function avisarCorrecao(motivos) {
    const [motivo] = motivos;

    console.warn('[kit] guarda corrigiu o carrinho', motivo);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'kit_carrinho_corrigido', motivo, correcoes: motivos });

    mostrarAviso(textoDoMotivo(motivo));
  }

  /**
   * Texto do aviso para o motivo. Os textos vêm do locale, pelos data-atributos
   * do `<script id="guarda-de-kit">`.
   * @param {string} motivo
   * @returns {string}
   */
  function textoDoMotivo(motivo) {
    if (motivo === 'cobranca_quantidade') return textosDoAviso.avisoCobrancaQuantidade ?? '';
    if (motivo === 'peca_sem_cobranca') return textosDoAviso.avisoPropriedades ?? '';

    return textosDoAviso.avisoCobrancaSemPeca ?? '';
  }

  /**
   * Toast mínimo, próprio da guarda: ela roda no site inteiro e não pode depender
   * de componente de nenhuma section. Fixo no rodapé acima da área segura do
   * iPhone, cabe em 375px com 16px de margem, acima da gaveta do carrinho; some
   * em ~6s e o toque o fecha.
   * @param {string} texto
   */
  function mostrarAviso(texto) {
    if (!texto || typeof document === 'undefined') return;

    document.getElementById('kit-carrinho-aviso')?.remove();

    const aviso = document.createElement('div');
    aviso.id = 'kit-carrinho-aviso';
    aviso.setAttribute('role', 'status');
    aviso.setAttribute('aria-live', 'polite');
    if (textosDoAviso.avisoFechar) aviso.setAttribute('aria-label', `${texto} ${textosDoAviso.avisoFechar}`);
    aviso.textContent = texto;
    aviso.style.cssText = [
      'position:fixed',
      'left:16px',
      'right:16px',
      'bottom:calc(16px + env(safe-area-inset-bottom, 0px))',
      'margin:0 auto',
      'max-width:420px',
      'box-sizing:border-box',
      'padding:12px 16px',
      'border-radius:10px',
      'background:#1a1a1a',
      'color:#fff',
      'font-size:15px',
      'line-height:1.4',
      'text-align:center',
      'z-index:2147483000',
      'cursor:pointer',
      'box-shadow:0 4px 16px rgba(0,0,0,.25)',
    ].join(';');

    const fechar = () => aviso.remove();
    aviso.addEventListener('click', fechar);
    document.body.appendChild(aviso);
    setTimeout(fechar, 6000);
  }

  /**
   * Chama a Cart AJAX API. Sem corpo é leitura.
   * @param {string} caminho
   * @param {object} [corpo]
   * @returns {Promise<object>}
   */
  async function pedir(caminho, corpo) {
    const resposta = await fetch(`${window.Shopify?.routes?.root ?? '/'}${caminho}`, {
      method: corpo ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(corpo ? { body: JSON.stringify(corpo) } : {}),
    });

    if (!resposta.ok) throw new Error(`${caminho} respondeu ${resposta.status}`);

    return resposta.json();
  }

  /**
   * Pede ao carrinho que se redesenhe. São três camadas coexistindo na loja e
   * nenhuma sabe da outra.
   * @param {object} carrinho
   */
  function redesenhar(carrinho) {
    window.kachingCartApi?.refreshCart?.();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: carrinho } }));

    // A página de carrinho do tema só se redesenha com o HTML das sections, que
    // o `/cart.js` não devolve. Recarregar é feio, mas é o que sobra.
    if (window.location.pathname.endsWith('/cart')) window.location.reload();
  }

  iniciar(document.getElementById('guarda-de-kit'));
})();
